import { Dialog } from '@headlessui/react';
import { useCallback, useRef, useState } from 'react';
import { MdWarningAmber } from 'react-icons/md';
import Modal from '../components/modal';

export type ConfirmDialogOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' (default) for destructive actions, 'warning' for actions the user can safely proceed with. */
  variant?: 'danger' | 'warning';
};

const VARIANT_CLASSES = {
  danger: {
    iconBg: 'bg-red-100',
    icon: 'text-red-600',
    confirmButton: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    focusRing: 'focus:ring-red-500',
  },
  warning: {
    iconBg: '',
    icon: 'text-yellow-600',
    confirmButton: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    focusRing: 'focus:ring-blue-500',
  },
};

/**
 * Confirmation dialog for destructive actions or warnings, as a replacement for
 * window.confirm. `confirm` opens the dialog and resolves true when the user
 * confirms, false when they cancel, press escape, or click the overlay.
 *
 * Render the returned `dialog` somewhere in the component:
 *
 *   const { confirm, dialog } = useConfirmDialog();
 *   const handleDelete = async () => {
 *     if (!(await confirm({ title: 'Delete this?', message: 'This cannot be undone.' }))) return;
 *     // ...
 *   };
 *   return <div>{dialog}</div>;
 */
export const useConfirmDialog = () => {
  const [open, setOpen] = useState(false);
  // Kept after closing so the dialog still renders its own content while it fades out.
  const [options, setOptions] = useState<ConfirmDialogOptions>();
  const resolveRef = useRef<(confirmed: boolean) => void>();
  // Focus Cancel on open so a stray Enter can't confirm the dialog.
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    resolveRef.current?.(confirmed);
    resolveRef.current = undefined;
    setOpen(false);
  }, []);

  const confirm = useCallback((confirmOptions: ConfirmDialogOptions) => {
    // A dialog that is somehow still waiting is treated as cancelled.
    resolveRef.current?.(false);
    setOptions(confirmOptions);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const classes = VARIANT_CLASSES[options?.variant || 'danger'];
  const isWarning = options?.variant === 'warning';

  // Only mounted while the dialog is open or closing, so the options are always set here.
  const content = (
    <>
      <div>
        {/* Warnings show the icon inline with the title; destructive actions show it above. */}
        {!isWarning && (
          <div
            className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${classes.iconBg}`}
          >
            <MdWarningAmber className={`h-6 w-6 ${classes.icon}`} aria-hidden='true' />
          </div>
        )}
        <div className={isWarning ? 'text-center' : 'mt-3 text-center sm:mt-5'}>
          <Dialog.Title
            as='h3'
            className='flex items-center justify-center gap-2 text-lg leading-6 font-medium text-gray-900'
          >
            {isWarning && (
              <MdWarningAmber className={`h-6 w-6 shrink-0 ${classes.icon}`} aria-hidden='true' />
            )}
            {options?.title}
          </Dialog.Title>
          <div className='mt-2'>
            <p className='text-sm text-gray-800'>{options?.message}</p>
          </div>
        </div>
      </div>
      <div
        className={`mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense ${
          isWarning ? 'sm:max-w-md sm:mx-auto' : ''
        }`}
      >
        <button
          type='button'
          className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:col-start-2 sm:text-sm ${classes.confirmButton}`}
          onClick={() => settle(true)}
        >
          {options?.confirmLabel || 'Delete'}
        </button>
        <button
          type='button'
          className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:col-start-1 sm:text-sm ${classes.focusRing}`}
          onClick={() => settle(false)}
          ref={cancelButtonRef}
        >
          {options?.cancelLabel || 'Cancel'}
        </button>
      </div>
    </>
  );

  const dialog = (
    <Modal
      content={content}
      initialFocusRef={cancelButtonRef}
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) settle(false);
      }}
    />
  );

  return { confirm, dialog };
};
